export type Portal = 'client' | 'platform';

export interface AuthUser {
  id: number;
  email: string;
  displayName: string;
  role: string;
  roleId?: number | null;
  /**
   * What this person may do, as sent back at sign-in.
   *
   * Presentation only. Hiding a button the API would refuse is a courtesy, not a
   * control — every one of these is checked again on the route, and the screens
   * are written on the assumption that this list can be stale or absent.
   */
  permissions?: string[];
  tenantId: number | null;
  tenantName: string | null;
  portal: Portal;
}

/** The permission keys the portal actually branches on. */
/**
 * The permission catalogue, mirrored for the portal.
 *
 * It was a partial mirror — thirteen of the server's eighteen — which is fine while
 * nothing needs the missing five and becomes a silent bug the moment something
 * does: `PERMISSION.contactsView` is `undefined`, `can(user, undefined)` is false,
 * and a destination disappears for everybody with no error anywhere.
 *
 * `tools/check-theme-tokens.mjs` is not the place for this; `PermissionCatalogueTests`
 * already checks the server's catalogue against migration 0023 and the route table,
 * and `tools/check-permission-mirror.mjs` now checks this file against the same
 * catalogue. Three places name these strings and none of them can drift silently.
 */
export const PERMISSION = {
  inboxView: 'inbox.view',
  inboxSend: 'inbox.send',
  inboxAssign: 'inbox.assign',
  contactsView: 'contacts.view',
  contactsWrite: 'contacts.write',
  contactsImport: 'contacts.import',
  contactsExport: 'contacts.export',
  segmentsWrite: 'segments.write',
  campaignsView: 'campaigns.view',
  campaignsWrite: 'campaigns.write',
  campaignsLaunch: 'campaigns.launch',
  templatesWrite: 'templates.write',
  tagsWrite: 'tags.write',
  cannedWrite: 'canned.write',
  automationWrite: 'automation.write',
  teamManage: 'team.manage',
  settingsWrite: 'settings.write',
  reportsView: 'reports.view',
} as const;

export function can(user: AuthUser | null | undefined, permission: string): boolean {
  return user?.permissions?.includes(permission) ?? false;
}

export interface Conversation {
  id: number;
  status: number;                 // 0 open, 1 pending, 2 resolved
  unreadCount: number;
  lastMessageAt: string;
  lastMessagePreview: string | null;
  lastMessageDirection: number | null;
  /** Not yet returned by the API — the list row skips the tick when absent. */
  lastMessageStatus?: number;
  lastInboundAt: string | null;
  lastOutboundAt: string | null;
  windowExpiresAt: string | null;
  assignedAgentId: number | null;
  assignedAgentName: string | null;
  waId: string;
  displayName: string | null;
  channelId: number;
  channelNumber: string | null;
  /** The number's verified name. The line under each row when a tenant runs more
      than one, and the reason every row otherwise looks like every other row. */
  channelName: string | null;
  tags: AppliedTag[];

  contactId: number;
  /** The customer's decision. Null when they have not made one. */
  optedOutAt: string | null;
  /** Ours. Deliberately a separate field from optedOutAt — see 0029. */
  blockedAt: string | null;
}

export interface Agent {
  id: number;
  displayName: string;
  email: string;
  role: string;
}

/** Keyset page. `nextCursor` null means the end — the client stops asking. */
/**
 * One of the tenant's own numbers.
 *
 * Quality and tier are here rather than only in the admin portal because they are
 * what decides whether this tenant's messages arrive, and the people sending them
 * were the last to know. Both come from Meta and are null until a sync has run —
 * a null tier is read by the dispatcher as the smallest one, which is why the home
 * screen says "not yet reported" rather than drawing a blank.
 */
export interface TenantChannel {
  id: number;
  displayPhoneNumber: string;
  verifiedName: string | null;
  /** GREEN, YELLOW or RED, as Meta words it. Null until first synced. */
  qualityRating: string | null;
  /** TIER_1K, TIER_10K, TIER_100K, TIER_UNLIMITED. Null until first synced. */
  messagingLimitTier: string | null;
  isActive: boolean;
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export interface Message {
  id: number;
  direction: number;
  source: number;
  messageType: string;
  body: string | null;
  status: number;
  errorCode: string | null;
  errorTitle: string | null;
  waMessageId: string | null;
  createdAt: string;
  sentByAgentId: number | null;
  sentByAgentName: string | null;

  /** Attachment, when the message carries one. */
  mediaAssetId: number | null;
  /** 0 pending, 1 stored, 2 failed — the bytes may not have arrived yet. */
  mediaStatus: number | null;
  mediaMimeType: string | null;
  mediaFileName: string | null;
  mediaByteSize: number | null;
  /** Signed, short-lived, ready to use directly in src. Null until stored. */
  mediaUrl: string | null;

  /** Everyone who has reacted to this message. Empty, never absent. */
  reactions: AppliedReaction[];
}

export interface AppliedReaction {
  emoji: string;
  /** 0 the contact, 1 one of our agents. */
  reactorKind: number;
  agentId: number | null;
  agentName: string | null;
  reactedAt: string;
}

/**
 * The six WhatsApp offers on a long-press, in its order.
 *
 * Deliberately not configurable. The value of a reaction bar is that the emoji
 * are in the same place every time, and a per-tenant list is a preference nobody
 * asked for that costs a settings screen and a migration.
 */
export const REACTION_CHOICES = ['\u{1F44D}', '\u2764\uFE0F', '\u{1F602}', '\u{1F62E}', '\u{1F622}', '\u{1F64F}'] as const;

/**
 * Collapses a message's reactions into one chip per distinct emoji.
 *
 * `mine` drives the highlighted state, and it is keyed on the agent id rather
 * than on "an agent reacted": in a shared inbox somebody else's thumbs-up must
 * not look like your own, or you will tap it to remove a reaction that is not
 * yours and nothing will happen.
 */
export function groupReactions(
  reactions: AppliedReaction[],
  myAgentId: number | null,
): { emoji: string; count: number; mine: boolean; who: string[] }[] {
  const groups = new Map<string, { emoji: string; count: number; mine: boolean; who: string[] }>();

  for (const r of reactions) {
    let group = groups.get(r.emoji);
    if (!group) {
      group = { emoji: r.emoji, count: 0, mine: false, who: [] };
      groups.set(r.emoji, group);
    }
    group.count += 1;
    if (r.reactorKind === 1 && r.agentId != null && r.agentId === myAgentId) group.mine = true;
    group.who.push(r.reactorKind === 0 ? 'Customer' : r.agentName ?? 'An agent');
  }

  return [...groups.values()];
}

/** What this agent has on a message right now, if anything. */
export function myReaction(reactions: AppliedReaction[], myAgentId: number | null): string | null {
  if (myAgentId == null) return null;
  return reactions.find((r) => r.reactorKind === 1 && r.agentId === myAgentId)?.emoji ?? null;
}

export function formatBytes(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export interface AdminOverview {
  activeTenants: number;
  activeChannels: number;
  conversations: number;
  messages: number;
  messages24h: number;
  failed24h: number;
  queueDepth: number;
  deadLetters: number;
  webhookBacklog: number;
  errors24h: number;
}

export interface AdminTenant {
  id: number; slug: string; companyName: string; contactEmail: string;
  status: number; createdAt: string; balance: number | null;
  channels: number; agents: number; messages30d: number;
}

export interface AdminChannel {
  id: number; tenantId: number; tenantName: string; wabaId: string;
  phoneNumberId: string; displayPhoneNumber: string | null; verifiedName: string | null;
  qualityRating: string | null; messagingLimitTier: string | null;
  sendRatePerSecond: number; isActive: boolean;
  metaAppName: string; webhookSlug: string;
  /** The client still answers this number in the WhatsApp Business app on a phone. */
  isCoexistence: boolean;
  /** 0 none asked for, 1 requested, 2 arriving, 3 done, 4 gave up. */
  historyState: number;
  historyMonths: number;
  historyMessages: number;
  historyError: string | null;
}

export const HISTORY_STATE: Record<number, string> = {
  0: 'not requested', 1: 'waiting for Meta', 2: 'arriving', 3: 'complete', 4: 'failed',
};

export interface AdminErrorEvent {
  id: number; correlationId: string | null; tenantId: number | null; tenantName: string | null;
  source: string; category: string; level: number; exceptionType: string | null;
  message: string; requestPath: string | null; occurredAt: string;
}

export interface AdminJobRun {
  id: number; jobName: string; status: number; startedAt: string;
  finishedAt: string | null; durationMs: number | null; itemsProcessed: number; detail: string | null;
}

export const MESSAGE_STATUS: Record<number, string> = {
  0: 'queued', 1: 'sent', 2: 'delivered', 3: 'read', 4: 'failed', 5: 'received',
};

export function isWindowOpen(c: Pick<Conversation, 'windowExpiresAt'>): boolean {
  return c.windowExpiresAt ? new Date(c.windowExpiresAt).getTime() > Date.now() : false;
}

export function windowRemaining(c: Pick<Conversation, 'windowExpiresAt'>): string | null {
  if (!c.windowExpiresAt) return null;
  const ms = new Date(c.windowExpiresAt).getTime() - Date.now();
  if (ms <= 0) return null;
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * A template as the picker needs it. `components` is deliberately not here: the
 * API resolves the shape at sync time, so nothing in the browser parses JSON to
 * draw a preview.
 */
export interface Template {
  id: number;
  name: string;
  language: string;
  category: string | null;
  status: string;
  bodyVariables: number;
  bodyText: string | null;
  headerText: string | null;
  footerText: string | null;
  headerFormat: string | null;
  /** Compact array for the preview, or null. Parsed once with `templateButtons`. */
  buttonsJson: string | null;
  needsHeaderMedia: boolean;
  needsButtonParams: boolean;
  rejectedReason: string | null;
  syncedAt: string | null;
}

export interface TemplateButton {
  type: string;
  text: string;
}

export function templateButtons(template: Pick<Template, 'buttonsJson'>): TemplateButton[] {
  if (!template.buttonsJson) return [];
  try {
    const parsed: unknown = JSON.parse(template.buttonsJson);
    return Array.isArray(parsed) ? (parsed as TemplateButton[]) : [];
  } catch {
    // A button we cannot read is one we do not draw. It never blocks the send.
    return [];
  }
}

/**
 * Why a template cannot be sent from this build, or null when it can.
 *
 * Returned as a sentence rather than a boolean so the picker can say what is
 * wrong instead of greying a row out with no explanation.
 */
export function templateBlockedReason(template: Template): string | null {
  if (template.status !== 'APPROVED') return `${template.status.toLowerCase()} at Meta`;
  if (template.needsHeaderMedia) return 'needs a header image';
  if (template.needsButtonParams) return 'has a button that takes a value';
  return null;
}

/**
 * Fills {{1}}, {{2}} … so the agent sees the message the customer will read.
 * Mirrors TemplateShape.Render on the server, including leaving an unfilled
 * placeholder visible rather than blanking it.
 */
export function renderTemplate(bodyText: string | null, variables: readonly string[]): string {
  if (!bodyText) return '';
  return bodyText.replace(/\{\{\s*(\d+)\s*\}\}/g, (match, digits: string) => {
    const index = Number(digits);
    const value = variables[index - 1];
    return index >= 1 && value !== undefined && value !== '' ? value : match;
  });
}

/** A template as the admin list shows it — everything, including retired ones. */
export interface AdminTemplate extends Template {
  metaTemplateId: string | null;
  componentsJson: string | null;
  authoredAt: string | null;
  authoredBy: string | null;
  needsHeaderText: boolean;
}

export type TemplateButtonType = 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER' | 'COPY_CODE';

export interface TemplateDraftButton {
  type: TemplateButtonType;
  text?: string;
  url?: string;
  example?: string;
  phoneNumber?: string;
}

export interface TemplateDraft {
  name: string;
  language: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  headerFormat: 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  headerText?: string;
  headerExample?: string;
  headerMediaAssetId?: number | null;
  body: string;
  bodyExamples: string[];
  footer?: string;
  buttons: TemplateDraftButton[];

  // Authentication templates only. WhatsApp writes and translates the message
  // itself and rejects any attempt to supply one, so these three settings are
  // the whole of what an author chooses.
  addSecurityRecommendation?: boolean;
  codeExpirationMinutes?: number | null;
  otpType?: 'COPY_CODE' | 'ONE_TAP';
  otpButtonText?: string;
  otpPackageName?: string;
  otpSignatureHash?: string;
  otpAutofillText?: string;
}

export function isAuthentication(draft: Pick<TemplateDraft, 'category'>): boolean {
  return draft.category === 'AUTHENTICATION';
}

/**
 * What WhatsApp will actually send for an authentication template. The wording is
 * Meta's and is translated per language; this is the English form, shown so the
 * author can see there is nothing for them to write.
 */
export function authenticationBody(draft: TemplateDraft, code = '123456'): string {
  return draft.addSecurityRecommendation
    ? `${code} is your verification code. For your security, do not share this code.`
    : `${code} is your verification code.`;
}

export const TEMPLATE_LANGUAGES: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'en_US', label: 'English (US)' },
  { code: 'en_GB', label: 'English (UK)' },
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'Hindi' },
  { code: 'mr', label: 'Marathi' },
  { code: 'gu', label: 'Gujarati' },
  { code: 'ta', label: 'Tamil' },
  { code: 'te', label: 'Telugu' },
  { code: 'kn', label: 'Kannada' },
  { code: 'ml', label: 'Malayalam' },
  { code: 'bn', label: 'Bengali' },
  { code: 'pa', label: 'Punjabi' },
  { code: 'ur', label: 'Urdu' },
  { code: 'ar', label: 'Arabic' },
];

/** Meta's own caps. Mirrored here so the form can count down as someone types. */
export const TEMPLATE_LIMITS = {
  header: 60,
  body: 1024,
  footer: 60,
  buttonText: 25,
  copyCode: 20,
  url: 2000,
} as const;

/**
 * Turns an existing template back into a draft, for editing or duplicating.
 * Reads the stored components rather than the flattened columns, because that is
 * the only place the button URLs and phone numbers survive.
 */
export function templateToDraft(template: AdminTemplate): TemplateDraft {
  const draft: TemplateDraft = {
    name: template.name,
    language: template.language,
    category: (template.category?.toUpperCase() as TemplateDraft['category']) ?? 'UTILITY',
    headerFormat: (template.headerFormat as TemplateDraft['headerFormat']) ?? 'NONE',
    headerText: template.headerText ?? undefined,
    body: template.bodyText ?? '',
    bodyExamples: Array.from({ length: template.bodyVariables }, () => ''),
    footer: template.footerText ?? undefined,
    buttons: [],
  };

  if (!template.componentsJson) return draft;

  try {
    const components: unknown = JSON.parse(template.componentsJson);
    if (!Array.isArray(components)) return draft;

    for (const component of components as Array<Record<string, unknown>>) {
      const type = String(component.type ?? '').toUpperCase();

      if (type === 'HEADER') {
        const example = component.example as { header_text?: string[] } | undefined;
        if (example?.header_text?.[0]) draft.headerExample = example.header_text[0];
      }

      if (type === 'BODY') {
        const example = component.example as { body_text?: string[][] } | undefined;
        const samples = example?.body_text?.[0];
        if (samples) {
          draft.bodyExamples = draft.bodyExamples.map((v, i) => samples[i] ?? v);
        }
      }

      if (type === 'BUTTONS' && Array.isArray(component.buttons)) {
        draft.buttons = (component.buttons as Array<Record<string, unknown>>).map((b) => ({
          type: String(b.type ?? 'QUICK_REPLY').toUpperCase() as TemplateButtonType,
          text: b.text as string | undefined,
          url: b.url as string | undefined,
          phoneNumber: b.phone_number as string | undefined,
          example: Array.isArray(b.example) ? String(b.example[0]) : (b.example as string | undefined),
        }));
      }
    }
  } catch {
    // A template we cannot read back opens as a blank draft rather than throwing
    // the editor away. The list still shows it correctly.
  }

  return draft;
}

export interface Tag {
  id: number;
  name: string;
  colour: string | null;
  conversationCount: number;
}

/** A tag as it arrives on a conversation — no usage count, it is not needed there. */
export interface AppliedTag {
  id: number;
  name: string;
  colour: string | null;
}

/**
 * Readable text for a tag chip, chosen from the background's luminance.
 *
 * The stored colour is whoever picked it's choice, and a pale one with white
 * text is unreadable. Deciding here means a bad colour is still a legible chip
 * rather than a support question.
 */
export function tagTextColour(colour: string | null): string {
  if (!colour || colour.length !== 7) return '#FFFFFF';

  const r = parseInt(colour.slice(1, 3), 16);
  const g = parseInt(colour.slice(3, 5), 16);
  const b = parseInt(colour.slice(5, 7), 16);
  if ([r, g, b].some(Number.isNaN)) return '#FFFFFF';

  // Rec. 601 luma — close enough for a chip, and cheap.
  return (r * 299 + g * 587 + b * 114) / 1000 > 145 ? '#1A1D23' : '#FFFFFF';
}

/**
 * The palette offered when creating a tag.
 *
 * A step lighter than it was. The chip renders the colour as a wash rather than a
 * fill now, and near-black source colours (#0F766E, #1D4ED8) produce a grey-looking
 * wash and text too dark to tell apart at chip size. These are the same hues one
 * step up in lightness, which keeps eight of them distinguishable when each is
 * only twelve per cent of the surface.
 */
export const TAG_COLOURS = [
  '#E2603A', '#D08A1E', '#2E9E4F', '#159A8C',
  '#3B7BE8', '#8558E0', '#D4488A', '#6B7A88',
] as const;

export interface Note {
  id: number;
  body: string;
  agentId: number;
  agentName: string | null;
  createdAt: string;
  /** Set once the note has been rewritten. Null means what it says is what was written. */
  editedAt: string | null;
  mentionedAgentIds: number[];
}

/**
 * One entry in the thread. A note sits in the same timeline as the messages
 * because it is almost always about the message above it — read anywhere else,
 * the reader has to work out what it referred to from its timestamp.
 */
export type ThreadEntry =
  | { kind: 'message'; at: number; message: Message }
  | { kind: 'note'; at: number; note: Note }
  | { kind: 'event'; at: number; event: ConversationEvent };

export function buildThread(
  messages: Message[], notes: Note[], events: ConversationEvent[] = [],
): ThreadEntry[] {
  const entries: ThreadEntry[] = [
    ...messages.map((message) => ({
      kind: 'message' as const, at: new Date(message.createdAt).getTime(), message,
    })),
    ...notes.map((note) => ({
      kind: 'note' as const, at: new Date(note.createdAt).getTime(), note,
    })),
    // The system pills. Interleaved by time like everything else, because "the
    // field was set, then she replied" and "she replied, then the field was set"
    // are different stories and the timeline is the only thing that tells them
    // apart.
    ...events.map((event) => ({
      kind: 'event' as const, at: new Date(event.createdAt).getTime(), event,
    })),
  ];

  return entries.sort((a, b) => a.at - b.at);
}

export interface CannedReply {
  id: number;
  shortcut: string;
  body: string;
  createdAt: string;
}

/**
 * Fills the one placeholder a quick reply supports.
 *
 * When the contact has no name the placeholder is left visible rather than
 * blanked — the text lands in the composer where the agent reads it before
 * sending, so a visible {{name}} gets caught and a silently empty one does not.
 */
export function fillQuickReply(body: string, contactName: string | null): string {
  if (!contactName) return body;
  return body.replace(/\{\{\s*name\s*\}\}/gi, contactName);
}

// ---------------------------------------------------------------- assignment --

export interface AgentGroup {
  id: number;
  name: string;
  members: { agentId: number; displayName: string; available: boolean }[];
}

export interface AssignmentRule {
  id: number;
  name: string;
  priority: number;
  isActive: boolean;
  matchChannelId: number | null;
  matchChannelLabel: string | null;
  matchTagId: number | null;
  matchTagLabel: string | null;
  matchKeyword: string | null;
  targetGroupId: number | null;
  targetGroupLabel: string | null;
}

/** What a rule matches on, in one word, for the list. */
export function ruleCondition(rule: AssignmentRule): string {
  if (rule.matchChannelId !== null) return `Sent to ${rule.matchChannelLabel ?? 'a number'}`;
  if (rule.matchTagId !== null) return `Tagged ${rule.matchTagLabel ?? 'a tag'}`;
  if (rule.matchKeyword) return `Message contains \u201c${rule.matchKeyword}\u201d`;
  return 'Everything else';
}

/**
 * The mistake the rule list has to warn about.
 *
 * A catch-all placed above a specific rule swallows it: first match wins, and a
 * rule with no condition matches everything. It is not an error — the tenant may
 * have meant it — so it is a warning on the row rather than a rejected save.
 */
export function shadowedRuleIds(rules: AssignmentRule[]): Set<number> {
  const shadowed = new Set<number>();
  const ordered = [...rules]
    .filter((r) => r.isActive)
    .sort((a, b) => a.priority - b.priority || a.id - b.id);

  const catchAllAt = ordered.findIndex(
    (r) => r.matchChannelId === null && r.matchTagId === null && !r.matchKeyword,
  );

  if (catchAllAt >= 0) {
    for (const rule of ordered.slice(catchAllAt + 1)) shadowed.add(rule.id);
  }

  return shadowed;
}

// ---------------------------------------------------------------------- team --

export interface TeamMember {
  id: number;
  displayName: string;
  email: string;
  /** The role's name, or null when the role it was on has been deleted. */
  role: string | null;
  roleId: number | null;
  supervisorId: number | null;
  /** Comma-separated channel ids, or null meaning every number. */
  channelIds: string | null;
  isActive: boolean;
  /** 0 Away, 1 Available. */
  availability: number;
  lastLoginAt: string | null;
  openConversations: number;
}

export interface PermissionDefinition {
  key: string;
  group: string;
  label: string;
  detail: string;
}

export interface Role {
  id: number;
  name: string;
  description: string | null;
  isSystem: boolean;
  agentCount: number;
  permissions: string[];
}

export interface TenantSettings {
  timeZone: string;
  hoursEnabled: boolean;
  awayMessage: string | null;
  holdAssignment: boolean;
  /**
   * The template the composer uses to reopen a closed window.
   *
   * Null when none is nominated **and** when the nominated one has since been
   * deleted or unapproved in Meta's console — the server joins rather than
   * echoing the column, so the composer falls back to the picker and says so
   * instead of sending an id that fails.
   */
  sessionOpenerTemplateId: number | null;

  /** While a flow is live on a conversation, routing waits for it. */
  botOwnsUntilHandoff: boolean;

  /**
   * Who may be given a new chat.
   *
   * 0 only people who are Available · 1 Available first, then anyone active ·
   * 2 anyone active, ignoring the Available/Away toggle entirely.
   *
   * 1 and 2 both mean a chat can land on somebody who is not at their desk — and
   * an assigned chat leaves the Unassigned queue the whole team watches. That is
   * the trade, and it is why 0 is the default.
   */
  assignmentPool: 0 | 1 | 2;

  /** 0 off · 1 the first time a chat reaches a person in a window · 2 every change of agent. */
  assignmentNoticeMode: 0 | 1 | 2;
  /** Sent inside the 24-hour window. `{{agent}}` is substituted. */
  assignmentNoticeText: string | null;
  /** Sent outside it. Null also means "nominated but no longer approved in Meta". */
  assignmentNoticeTemplateId: number | null;

  /** 0 nothing · 1 the custom text · 2 the working-hours away message. */
  noAgentNoticeMode: 0 | 1 | 2;
  noAgentNoticeText: string | null;
}

/** One flow, as the routing screen needs to know about it. */
export interface RoutingFlow {
  id: number;
  name: string;
  isEnabled: boolean;
  channelId: number | null;
  channelName: string | null;
  /**
   * Whether the flow contains a handoff step. False means it can never reach a
   * person on its own — with bot-first ordering the rules are never asked, and
   * every conversation waits in Unassigned for somebody to open it.
   */
  hasHandoff: boolean;
}

export interface WorkingHour {
  id: number;
  channelId: number | null;
  dayOfWeek: number;
  opensAt: string;
  closesAt: string;
}

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Can this member be demoted or deactivated without stranding the tenant?
 *
 * Mirrors the server's guard so the UI can grey the control and say why, rather
 * than offering it and reporting a 409. The server check is the real one — this is
 * only the explanation.
 */
export function isLastAdministrator(team: TeamMember[], member: TeamMember, roles: Role[]): boolean {
  // "Administrator" is no longer a role name, it is whoever can manage the team.
  // A tenant that renamed the idea, or split it across two composed roles, must not
  // be told it is about to strand itself when it is not.
  const managing = new Set(roles.filter((r) => r.permissions.includes('team.manage')).map((r) => r.id));

  if (!member.isActive || member.roleId === null || !managing.has(member.roleId)) return false;

  return team.filter((m) => m.isActive && m.roleId !== null && managing.has(m.roleId)).length === 1;
}

// -------------------------------------------------------------- provisioning --

export interface MetaApp {
  id: number;
  name: string;
  metaAppId: string;
  webhookSlug: string;
  graphVersion: string;
  isActive: boolean;
  /** Null means every client may route through it; a value means exactly one may. */
  tenantId: number | null;
  tenantName: string | null;
  hasAppSecret: number;
  channels: number;
  /** Meta's Login-for-Business configuration id. Null until the app is an approved Tech Provider. */
  esConfigId: string | null;
}

export interface ChannelCheck {
  ok: boolean;
  problem: string | null;
  verifiedName: string | null;
  displayPhoneNumber: string | null;
  qualityRating: string | null;
  throughputLevel: string | null;
  suggestedRatePerSecond: number;
}

/** Which apps a given client may route through: the platform's, plus their own. */
export function appsAvailableTo(apps: MetaApp[], tenantId: number | null): MetaApp[] {
  return apps.filter((a) => a.isActive && (a.tenantId === null || a.tenantId === tenantId));
}

// ----------------------------------------------------------------- schedule --

export interface ScheduledMessage {
  id: number;
  scheduledFor: string;
  messageId: number;
  conversationId: number;
  messageType: string;
  body: string | null;
  contactName: string | null;
  waId: string;
  scheduledBy: string | null;
  windowExpiresAt: string | null;
}

/**
 * Will this probably be refused when it fires?
 *
 * A warning, never a refusal. The window may well be open again by then, because
 * customers reply — and a tool that forbids something likely to work is worse than
 * one that mentions it. A template is exempt: that is the thing that works outside
 * the window, and it is what the warning points at.
 */
export function scheduledPastWindow(
  scheduledFor: string,
  windowExpiresAt: string | null,
  messageType = 'text',
): boolean {
  if (messageType === 'template') return false;
  if (!windowExpiresAt) return true;
  return new Date(scheduledFor).getTime() > new Date(windowExpiresAt).getTime();
}

// ----------------------------------------------------------------- contacts --

export interface Contact {
  id: number;
  waId: string;
  displayName: string | null;
  /** The custom-field bag, as the server stores it: JSON text, or null. */
  attributes: string | null;
  optedOutAt: string | null;
  optOutReason: string | null;
  optOutMessage?: string | null;
  optOutBy?: string | null;
  lastSeenAt: string | null;
  createdAt: string;
}

export interface ContactField {
  id: number;
  fieldKey: string;
  label: string;
  fieldType: 'text' | 'number' | 'date' | 'list' | 'boolean';
  options: string | null;
  sortOrder: number;
}

/**
 * The bag, parsed, without throwing.
 *
 * A contact whose attributes are unreadable still has a phone number and a name,
 * and the screen has to render them. Returning {} loses one cell; throwing loses
 * the row and, in a list, every row after it.
 */
export function contactAttributes(contact: Pick<Contact, 'attributes'>): Record<string, string> {
  if (!contact.attributes) return {};
  try {
    const parsed: unknown = JSON.parse(contact.attributes);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>)
        .filter(([, v]) => v !== null && v !== undefined)
        .map(([k, v]) => [k, String(v)]),
    );
  } catch {
    return {};
  }
}

/** The options of a list field, which the server stores as a JSON array. */
export function fieldOptions(field: ContactField): string[] {
  if (!field.options) return [];
  try {
    const parsed: unknown = JSON.parse(field.options);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

// ----------------------------------------------------------------- segments --

/** One clause. The server ANDs them; there is no OR, by design. */
export interface SegmentRule {
  kind: 'tag' | 'field' | 'opted_out' | 'last_inbound' | 'channel';
  op: string;
  field?: string | null;
  value?: string | null;
}

export interface Segment {
  id: number;
  name: string;
  /** 0 static — a fixed list. 1 dynamic — a saved question. */
  kind: 0 | 1;
  rules: string | null;
  createdAt: string;
  createdBy: string | null;
  /** Null for a dynamic segment: counting one costs a query, so it is asked for. */
  memberCount: number | null;
}

export function segmentRules(segment: Pick<Segment, 'rules'>): SegmentRule[] {
  if (!segment.rules) return [];
  try {
    const parsed: unknown = JSON.parse(segment.rules);
    return Array.isArray(parsed) ? (parsed as SegmentRule[]) : [];
  } catch {
    return [];
  }
}

// ------------------------------------------------------------------ imports --

export interface ImportJob {
  id: number;
  fileName: string;
  /** 0 pending, 1 running, 2 done, 3 failed. */
  status: 0 | 1 | 2 | 3;
  totalRows: number;
  processed: number;
  imported: number;
  updated: number;
  skipped: number;
  lastError: string | null;
  createdAt: string;
  completedAt: string | null;
  startedBy?: string | null;
}

export interface ImportProblem {
  lineNumber: number;
  reason: string;
  raw: string | null;
}

export interface ImportPreview {
  headers: string[];
  sample: string[][];
  totalRows: number;
  /** The file itself, handed back so the confirm step does not re-upload it. */
  content: string;
}

/**
 * A guess at which CSV column is which field.
 *
 * Only a guess: it is the starting position of a mapping screen the user can
 * change, not a decision. Getting it right for the common header names is the
 * difference between an import that takes four clicks and one that takes twelve.
 */
export function guessMapping(headers: string[], fields: ContactField[]): Record<string, number> {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const mapping: Record<string, number> = {};

  const find = (candidates: string[]) =>
    headers.findIndex((h) => candidates.includes(norm(h)));

  const phone = find(['phone', 'phonenumber', 'mobile', 'mobileno', 'whatsapp', 'whatsappnumber', 'number', 'contact', 'msisdn', 'waid']);
  if (phone >= 0) mapping.phone = phone;

  const name = find(['name', 'fullname', 'firstname', 'displayname', 'customername', 'contactname']);
  if (name >= 0) mapping.name = name;

  for (const field of fields) {
    const index = headers.findIndex((h) => norm(h) === norm(field.label) || norm(h) === norm(field.fieldKey));
    if (index >= 0) mapping[field.fieldKey] = index;
  }

  return mapping;
}

// ---------------------------------------------------------------- campaigns --

/** Where one template variable's value comes from. */
export interface VariableBinding {
  source: 'name' | 'field' | 'literal';
  key?: string | null;
  value?: string | null;
  /** Used when the contact has nothing for this binding. Never empty. */
  fallback: string;
}

export type CampaignStatus = 0 | 1 | 2 | 3 | 4 | 5;

export interface Campaign {
  id: number;
  name: string;
  /** 0 draft, 1 scheduled, 2 running, 3 paused, 4 completed, 5 cancelled. */
  status: CampaignStatus;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  deliveredCount: number;
  readCount: number;
  clickedCount?: number;
  estimatedCost: number | null;
  /** wallet | tier | agent. */
  pauseReason: string | null;
  /** Set when the number has hit its 24-hour ceiling and the rest waits. */
  resumeAfter: string | null;
  createdAt: string;
  templateName: string;
  fromNumber: string | null;
  createdBy?: string | null;
}

export interface CampaignDetail extends Campaign {
  channelId: number;
  templateId: number;
  segmentId: number | null;
  retargetOfCampaignId: number | null;
  retargetOutcome: string | null;
  variableMap: string | null;
  frozenAt: string | null;
  templateBody: string | null;
  bodyVariables: number;
  templateStatus: string | null;
}

export interface CampaignClickSummary {
  buttonPayload: string;
  buttonText: string | null;
  clicks: number;
}

export interface CampaignEstimate {
  recipients: number;
  unitPrice: number;
  cost: number;
  balance: number;
  affordable: boolean;
  problem: string | null;
}

export interface CampaignPreview {
  contact: { id: number; waId: string; displayName: string | null } | null;
  values: string[];
  /** How many values came from the fallback rather than from the contact. */
  usedFallback: number;
  body: string;
}

export interface CampaignRecipient {
  id: number;
  contactId: number;
  waId: string | null;
  /** 0 pending, 1 queued, 2 sent, 3 failed, 4 skipped. */
  status: 0 | 1 | 2 | 3 | 4;
  failureReason: string | null;
  processedAt: string | null;
  displayName: string | null;
  /** From the message: 2 delivered, 3 read, 4 failed. Null until it is sent. */
  messageStatus: number | null;
  errorTitle: string | null;
}

export const CAMPAIGN_STATUS: Record<CampaignStatus, string> = {
  0: 'Draft',
  1: 'Scheduled',
  2: 'Sending',
  3: 'Paused',
  4: 'Sent',
  5: 'Cancelled',
};

export function parseVariableMap(json: string | null | undefined): VariableBinding[] {
  if (!json) return [];
  try {
    const parsed: unknown = JSON.parse(json);
    return Array.isArray(parsed) ? (parsed as VariableBinding[]) : [];
  } catch {
    return [];
  }
}

/**
 * Why this campaign cannot be launched yet, or null.
 *
 * The same rules the server enforces, said before the button is pressed rather
 * than after. The server is still the authority — this only saves a round trip
 * and, more usefully, explains what to fix while the form is still open.
 */
export function campaignBlockers(
  map: VariableBinding[],
  bodyVariables: number,
  hasAudience: boolean,
  templateStatus: string | null,
): string[] {
  const problems: string[] = [];

  if (!hasAudience) problems.push('Choose who this goes to.');

  if (templateStatus && templateStatus.toUpperCase() !== 'APPROVED') {
    problems.push(`This template is ${templateStatus.toLowerCase()} and cannot be sent.`);
  }

  if (map.length !== bodyVariables) {
    problems.push(`This template takes ${bodyVariables} value${bodyVariables === 1 ? '' : 's'}.`);
    return problems;
  }

  map.forEach((binding, i) => {
    if (!binding.fallback?.trim()) {
      problems.push(`Value ${i + 1} needs a fallback for contacts that have nothing to put there.`);
    }
    if (binding.source === 'field' && !binding.key) {
      problems.push(`Value ${i + 1} is set to a contact field but names none.`);
    }
    if (binding.source === 'literal' && !binding.value?.trim()) {
      problems.push(`Value ${i + 1} is a fixed value, which cannot be blank.`);
    }
  });

  return problems;
}

/** The variable positions a template body actually uses, in order: {{1}}, {{2}}… */
export function templateVariablePositions(body: string | null | undefined): number[] {
  if (!body) return [];
  const found = new Set<number>();
  for (const match of body.matchAll(/\{\{(\d+)\}\}/g)) found.add(Number(match[1]));
  return [...found].sort((a, b) => a - b);
}

// --------------------------------------------------------------------- bots --

export interface BotFlowSummary {
  id: number;
  name: string;
  version: number;
  isPublished: boolean;
  isEnabled: boolean;
  channelId: number | null;
  channelNumber: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** How many conversations are mid-way through this exact version right now. */
  liveSessions: number;
}

export interface BotFlow {
  id: number;
  tenantId: number;
  name: string;
  version: number;
  isPublished: boolean;
  isEnabled: boolean;
  channelId: number | null;
  definition: string;
  publishedAt: string | null;
  updatedAt: string | null;
}

export interface FlowReport {
  ok: boolean;
  errors: string[];
  warnings: string[];
}

export interface BotAction {
  id: number;
  actionKey: string;
  name: string;
  description: string | null;
  config: string;
  authType: string | null;
  authHeader: string | null;
  /** The credential itself is never sent to a browser. This only says whether one exists. */
  hasSecret: boolean;
  isActive: boolean;
  updatedAt: string;
}

export interface BotSessionRow {
  conversationId: number;
  state: string;
  variables: string | null;
  stepCount: number;
  updatedAt: string;
  handedOffAt: string | null;
  flowName: string;
  flowVersion: number;
  contactName: string | null;
  waId: string;
}

/** Every node type the interpreter understands. */
export const FLOW_NODE_TYPES = [
  'message', 'list', 'buttons', 'capture', 'condition',
  'action', 'set', 'dynamic_list', 'handoff', 'end',
] as const;

export type FlowNodeType = (typeof FLOW_NODE_TYPES)[number];

export interface FlowDefinition {
  start?: string;
  triggers?: string[];
  resume?: Record<string, unknown>;
  nodes?: Record<string, FlowNode>;
}

export interface FlowNode {
  type?: FlowNodeType;
  [key: string]: unknown;
}

export function parseFlow(json: string | null | undefined): FlowDefinition {
  if (!json) return { nodes: {} };
  try {
    const parsed: unknown = JSON.parse(json);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as FlowDefinition)
      : { nodes: {} };
  } catch {
    return { nodes: {} };
  }
}

/**
 * Every node one node can lead to, however it leads there.
 *
 * The same set the server walks for reachability. Kept in step deliberately: the map
 * drawing different edges from the ones the validator checks would make an
 * unreachable node look connected.
 */
export function flowTargets(node: FlowNode | undefined): string[] {
  if (!node) return [];

  const targets: unknown[] = [node.next, node.default, node.empty_next];

  for (const key of ['transitions', 'cases'] as const) {
    const map = node[key];
    if (map && typeof map === 'object') targets.push(...Object.values(map as Record<string, unknown>));
  }

  if (Array.isArray(node.extra_rows)) {
    for (const extra of node.extra_rows as Record<string, unknown>[]) targets.push(extra?.target);
  }

  return targets.filter((t): t is string => typeof t === 'string' && t.length > 0);
}

/**
 * Lays the flow out in columns by distance from the start.
 *
 * A breadth-first walk rather than a physics simulation: a flow is a directed graph
 * that people read top to bottom, and a force-directed layout of the same 64 nodes
 * puts them in a cloud nobody can follow. Anything unreachable is placed in its own
 * column at the end, which is exactly the thing you want to see.
 */
export function layoutFlow(definition: FlowDefinition): {
  nodes: { id: string; column: number; row: number; node: FlowNode; unreachable: boolean }[];
  edges: { from: string; to: string; label?: string }[];
  columns: number;
} {
  const nodes = definition.nodes ?? {};
  const ids = Object.keys(nodes);
  const start = definition.start && nodes[definition.start] ? definition.start : ids[0];

  const depth = new Map<string, number>();
  if (start) {
    const queue: [string, number][] = [[start, 0]];
    while (queue.length > 0) {
      const [id, d] = queue.shift()!;
      if (depth.has(id)) continue;
      depth.set(id, d);
      for (const target of flowTargets(nodes[id])) {
        if (nodes[target] && !depth.has(target)) queue.push([target, d + 1]);
      }
    }
  }

  const maxDepth = depth.size > 0 ? Math.max(...depth.values()) : 0;

  const rowsPerColumn = new Map<number, number>();
  const reachable = ids.filter((id) => depth.has(id));

  const placed = reachable.map((id) => {
    const column = depth.get(id)!;
    const row = rowsPerColumn.get(column) ?? 0;
    rowsPerColumn.set(column, row + 1);
    return { id, column, row, node: nodes[id]!, unreachable: false };
  });

  // Unreachable nodes go in the first column, below everything else — not in a
  // column of their own past the end. Finding what nothing points at is the main
  // reason to look at the map at all, and parking it off the right-hand edge of a
  // sixty-node diagram means scrolling past the entire flow to discover it.
  const deepestRow = Math.max(0, ...placed.map((n) => n.row + 1));
  ids.filter((id) => !depth.has(id)).forEach((id, i) => {
    placed.push({ id, column: 0, row: deepestRow + i, node: nodes[id]!, unreachable: true });
  });

  // One line per pair, not one per way of getting there.
  //
  // A condition with two cases leading to the same step, or a menu with two options
  // that do, is one connection on the page — drawing it twice lays identical curves
  // on top of each other, which darkens the stroke and reads as a stronger link, and
  // makes the caption claim more connections than the flow has. The imported Carysil
  // flow has 104 ways to move between 98 pairs of steps, and said 104.
  const seen = new Set<string>();
  const edges: { from: string; to: string; label?: string }[] = [];
  for (const id of ids) {
    for (const target of flowTargets(nodes[id])) {
      const key = `${id}\u0000${target}`;
      if (!nodes[target] || seen.has(key)) continue;
      seen.add(key);
      edges.push({ from: id, to: target });
    }
  }

  return { nodes: placed, edges, columns: maxDepth + 1 };
}

// ------------------------------------------------------------------ billing --

export interface Wallet {
  balance: number;
  /** Money committed to messages that have been accepted but not yet sent. */
  held: number;
  /** balance - held. What a new send is actually checked against. */
  available: number;
  isActive: boolean;
  lowBalanceAlert: number;
  suspensionReason: string | null;
}

/** 0 debit, 1 credit, 2 refund, 3 maintenance, 4 adjustment. */
export interface LedgerEntry {
  id: number;
  kind: number;
  amount: number;
  balanceAfter: number;
  externalRef: string | null;
  description: string | null;
  createdAt: string;
  messageId: number | null;
  templateName: string | null;
}

export interface Statement {
  month: string;
  opening: number;
  closing: number;
  totals: { spent: number | null; added: number | null; adjusted: number | null; entries: number };
  byCategory: { category: string; messages: number; amount: number }[];
}

export interface BillingProfile {
  legalName: string;
  gstin: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  stateCode: string | null;
  postalCode: string | null;
  billingEmail: string | null;
}

export interface Invoice {
  id: number;
  number: string;
  issuedAt: string;
  taxableAmount: number;
  cgst: number;
  sgst: number;
  igst: number;
  total: number;
  status: number;
}

export interface TopUpOrder {
  paymentId: number;
  orderId: string;
  keyId: string;
  taxable: number;
  gross: number;
  cgst: number;
  sgst: number;
  igst: number;
  currency: string;
}

export interface CategoryPrice {
  category: string;
  unitPrice: number;
  defaultPrice?: number;
  isOverride?: boolean;
  updatedAt: string | null;
}

/**
 * GST state codes. Only the ones a client is plausibly registered in — the list is
 * for a picker, not for validation, which the server does by length.
 */
export const GST_STATES: { code: string; name: string }[] = [
  { code: '27', name: 'Maharashtra' },
  { code: '29', name: 'Karnataka' },
  { code: '07', name: 'Delhi' },
  { code: '33', name: 'Tamil Nadu' },
  { code: '36', name: 'Telangana' },
  { code: '24', name: 'Gujarat' },
  { code: '09', name: 'Uttar Pradesh' },
  { code: '19', name: 'West Bengal' },
  { code: '08', name: 'Rajasthan' },
  { code: '32', name: 'Kerala' },
  { code: '23', name: 'Madhya Pradesh' },
  { code: '06', name: 'Haryana' },
  { code: '03', name: 'Punjab' },
  { code: '30', name: 'Goa' },
  { code: '10', name: 'Bihar' },
  { code: '21', name: 'Odisha' },
  { code: '20', name: 'Jharkhand' },
  { code: '22', name: 'Chhattisgarh' },
  { code: '02', name: 'Himachal Pradesh' },
  { code: '05', name: 'Uttarakhand' },
  { code: '04', name: 'Chandigarh' },
  { code: '18', name: 'Assam' },
];

export const LEDGER_KINDS: Record<number, string> = {
  0: 'Sent',
  1: 'Top-up',
  2: 'Refund',
  3: 'Correction',
  4: 'Adjustment',
};

/** Indian grouping, two decimals, no currency symbol duplication. */
export function formatMoney(value: number | null | undefined): string {
  return (value ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ---------------------------------------------------------------- analytics --

export interface ReportDay {
  day: string;
  inbound: number;
  outbound: number;
  failed: number;
  spend: number;
  opened: number;
  resolved: number;
  reopened: number;
}

export interface ReportTotals {
  inbound: number;
  outbound: number;
  failed: number;
  spend: number;
  opened: number;
  resolved: number;
  reopened: number;
  /** Minutes. Null when nothing in the range could contribute a figure. */
  firstResponseOpen: number | null;
  firstResponseWall: number | null;
  resolutionOpen: number | null;
  resolutionWall: number | null;
  measuredResponses: number;
  measuredResolutions: number;
  unknownResponses: number;
  unknownResolutions: number;
}

export interface ReportOverview {
  from: string;
  to: string;
  /** The last day of the range has not finished yet. */
  includesToday: boolean;
  days: ReportDay[];
  totals: ReportTotals;
}

export interface AgentReportRow {
  agentId: number;
  displayName: string;
  messagesSent: number;
  resolved: number;
  assigned: number;
  frSum: number;
  frCount: number;
  resSum: number;
  resCount: number;
}

export interface TemplateReportRow {
  templateName: string;
  sent: number;
  delivered: number;
  readCount: number;
  failed: number;
  clicked: number;
  spend: number;
}

export interface SlaPolicy {
  firstResponseMinutes: number | null;
  resolutionMinutes: number | null;
  /** 0 business hours, 1 wall clock. */
  primaryClock: number;
  isActive: boolean;
}

/**
 * Minutes as something a person reads. Deliberately coarse above an hour: "2h 14m"
 * is what somebody wants from a response time, and "134 minutes" is arithmetic they
 * have to do themselves.
 */
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes < 1) return 'under a minute';
  if (minutes < 60) return `${Math.round(minutes)}m`;

  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);

  if (hours < 24) return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;

  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

// ------------------------------------------------------------ platform admin --

/** One row of the audit trail. */
export interface AuditRow {
  id: number;
  tenantId: number | null;
  tenantName: string | null;
  /** 0 platform user, 1 agent, 2 API key, 3 the platform itself. */
  actorType: number;
  actorId: number | null;
  /** The actor's name as it was at the time, not as it is now. */
  actorLabel: string | null;
  action: string;
  resourceType: string | null;
  resourceId: number | null;
  /** JSON: what changed, not the whole row. */
  detail: string | null;
  ipAddress: string | null;
  createdAt: string;
}

export interface AuditPage {
  rows: AuditRow[];
  nextBeforeId: number | null;
}

export interface PlatformDay {
  day: string;
  inbound: number;
  outbound: number;
  failed: number;
  spend: number;
}

export interface PlatformTotals {
  inbound: number;
  outbound: number;
  failed: number;
  /** The headline. Exclusive of GST, because the tax is not ours. */
  cashReceived: number;
  invoiced: number;
  creditConsumed: number;
  paymentCount: number;
  activeTenants: number;
  suspendedTenants: number;
  /** Paid for and not yet used. A liability, not revenue. */
  outstandingCredit: number;
}

export interface PlatformOverview {
  from: string;
  to: string;
  days: PlatformDay[];
  totals: PlatformTotals;
}

export interface TenantMoneyRow {
  tenantId: number;
  companyName: string;
  status: number;
  balance: number;
  held: number;
  messagesSent: number;
  creditConsumed: number;
  cashReceived: number;
}

export interface PlatformUser {
  id: number;
  email: string;
  displayName: string;
  isSuperuser: boolean;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

/** 0 suspended, 1 active, 2 closed. Closed is not deleted: a tenant owns history. */
export const TENANT_STATUS: Record<number, string> = {
  0: 'Suspended',
  1: 'Active',
  2: 'Closed',
};

export const AUDIT_ACTOR: Record<number, string> = {
  0: 'Platform',
  1: 'Agent',
  2: 'API key',
  3: 'System',
};

/**
 * The four groups the audit screen filters by, as action prefixes.
 *
 * Mirrors AuditActions.Groups on the server. It is duplicated rather than fetched
 * because it is a label on a dropdown, and a screen that cannot draw its own filter
 * until a request comes back is a screen that flickers.
 */
export const AUDIT_GROUPS: { key: string; label: string; prefixes: string[] }[] = [
  { key: 'access', label: 'Permissions and access', prefixes: ['role.', 'agent.', 'password.'] },
  { key: 'money', label: 'Money and pricing', prefixes: ['pricing.', 'wallet.', 'invoice.', 'tenant.suspended', 'tenant.reactivated'] },
  { key: 'provisioning', label: 'Provisioning', prefixes: ['tenant.created', 'tenant.updated', 'tenant.closed', 'meta_app.', 'channel.'] },
  { key: 'bulk', label: 'Bulk customer actions', prefixes: ['campaign.', 'contacts.', 'retention.'] },
];

// ------------------------------------------------------------- developer API --

export interface ApiKeySummary {
  id: number;
  name: string;
  keyPrefix: string;
  /** Space-separated permissions, or null meaning every permission — a key issued before scopes existed. */
  scopes: string | null;
  ratePerMinute: number;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface WebhookSubscription {
  id: number;
  url: string;
  /** Space-separated event names. Empty means every event. */
  events: string;
  isActive: boolean;
  consecutiveFailures: number;
  pausedAt: string | null;
  pauseReason: string | null;
  failedCount: number;
  createdAt: string;
}

export interface WebhookDelivery {
  id: number;
  eventId: string;
  event: string;
  /** 0 waiting, 1 delivered, 2 gave up. */
  status: number;
  attempts: number;
  lastStatusCode: number | null;
  lastError: string | null;
  nextAttemptAt: string;
  createdAt: string;
  deliveredAt: string | null;
}

// ------------------------------------------------------------------- inbox --

/**
 * One system pill in a thread.
 *
 * `detail` is JSON and records values **as they were set**, never a reference —
 * a custom field renamed next year must not rewrite what a pill said last Tuesday.
 */
export interface ConversationEvent {
  id: number;
  kind: string;
  detail: string | null;
  /** 0 platform user, 1 agent, 2 API key, 3 the platform itself. */
  actorType: number;
  actorId: number | null;
  actorLabel: string | null;
  createdAt: string;
}

export interface Collaborator {
  agentId: number;
  displayName: string;
  email: string;
  isActive: boolean;
  addedAt: string;
  addedByName: string | null;
}

export interface BotRun {
  id: number;
  flowId: number | null;
  flowName: string | null;
  flowVersion: number | null;
  currentNode: string | null;
  status: number;
  variables: string | null;
  startedAt: string;
  updatedAt: string;
}

/**
 * A system pill, as a sentence.
 *
 * The server stores a kind and a JSON detail; the wording lives here because it is
 * wording. A kind the client does not know renders as nothing at all — a silent gap
 * in the timeline — so the fallback is the kind itself rather than an empty string.
 */
export function describeEvent(event: ConversationEvent): string {
  let detail: Record<string, string> = {};
  try {
    detail = event.detail ? (JSON.parse(event.detail) as Record<string, string>) : {};
  } catch {
    detail = {};
  }

  const by = event.actorLabel ? ` by ${event.actorLabel}` : '';

  switch (event.kind) {
    case 'field.set':
      return `Custom field "${detail.field}" set to "${detail.value ?? ''}"${by}`;
    case 'assigned':
      return `Assigned to ${detail.agent ?? 'somebody'}${by}`;
    case 'unassigned':
      return `Unassigned${by}`;
    case 'resolved':
      return `Marked resolved${by}`;
    case 'reopened':
      return 'Reopened by a new message';
    case 'collaborator.added':
      return `${detail.agent ?? 'Somebody'} added as a collaborator${by}`;
    case 'collaborator.removed':
      return `${detail.agent ?? 'Somebody'} removed as a collaborator${by}`;
    case 'tag.added':
      return `Tagged "${detail.tag}"${by}`;
    case 'tag.removed':
      return `Tag "${detail.tag}" removed${by}`;
    case 'opted_out':
      return `Opted out of messages${by}`;
    case 'bot.handoff':
      return 'Handed over from the bot';
    // Blocked is ours, opted_out is theirs — the two sentences say which, because
    // a timeline that renders both as "stopped messaging" loses the only fact a
    // reader of it needs.
    case 'blocked':
      return detail.reason
        ? `Blocked${by} — ${detail.reason}`
        : `Blocked${by}`;
    case 'unblocked':
      return `Unblocked${by}`;
    case 'chat.cleared':
      return `Earlier messages hidden from this pane${by}`;
    default:
      return event.kind;
  }
}
